// Component: StatTile.jsx
// Purpose: A "number-as-hero" stat block — an eyebrow label over a big tabular
//   figure, with an optional signed delta chip and/or a Sparkline underneath.
//   The figure itself stays monochrome (fintech-clean doctrine: green/red is
//   carried by the signed delta chip, not the base number). Sizes map to the
//   .ds-num-hero/xl/lg numeric classes. Styling in ds-components.css
//   (.ds-stat-tile-*). `NumberHero` is the size="hero" preset.
//
// Usage:
//   <StatTile eyebrow="Today's P&L" value="+₹12,847" size="hero"
//             delta={{ text: "+2.34%", sign: 1 }} sparkline={series} />
//   <NumberHero eyebrow="Balance" value="₹4,82,190" />

import React from "react";
import { cx } from "./cx";
import { Sparkline } from "./Sparkline";
import { RollingNumber } from "./RollingNumber";

const NUM = { hero: "ds-num-hero", xl: "ds-num-xl", lg: "ds-num-lg" };
const SIGN = { "1": "pos", "-1": "neg", "0": "flat" };

export function StatTile({
  eyebrow,
  value,
  size = "lg",
  delta,
  sparkline,
  rolling = false,
  children,
  className,
  ...props
}) {
  const signKey = delta ? SIGN[String(delta.sign)] || "flat" : null;
  // `auto` lets the sparkline color itself; if a delta sign exists, match it.
  const sparkTint = delta ? (delta.sign > 0 ? "profit" : delta.sign < 0 ? "loss" : "mono") : "auto";

  return (
    <div className={cx("ds-stat-tile", className)} {...props}>
      {eyebrow != null && <div className="ds-stat-tile-eyebrow">{eyebrow}</div>}
      <div className={cx("ds-stat-tile-value", NUM[size] || NUM.lg)}>
        {rolling ? <RollingNumber value={value} /> : value}
      </div>
      {delta != null && (
        <span className={cx("ds-stat-tile-delta", `sign-${signKey}`)}>{delta.text}</span>
      )}
      {Array.isArray(sparkline) && sparkline.length > 1 && (
        <div className="ds-stat-tile-spark">
          <Sparkline data={sparkline} tint={sparkTint} fill />
        </div>
      )}
      {children}
    </div>
  );
}

// Preset: hero-sized figure. Explicit `size` on the caller still wins.
export const NumberHero = (props) => <StatTile size="hero" {...props} />;

export default StatTile;
